/**
 * ConfirmationCardDiff — the visual old → new field-change renderer.
 *
 * Stream 11 splits this out from <ConfirmationCard> so the diff block
 * is independently re-usable (Phase 2 may render multiple diffs in a
 * single card — e.g., a tool that sets two fields at once — without
 * forking the card chrome).
 *
 * VISUAL CONTRACT (phase-1-spec §6 / copy-deck §5.1):
 *   - Old value: rendered inside <del> with strikethrough, muted color.
 *     Using a real <del> element (not just a CSS strikethrough) means
 *     screen readers announce "deletion" without us having to layer
 *     aria-* attributes on top.
 *   - Arrow separator: "→" (U+2192). Hidden from AT via aria-hidden so
 *     SR users hear "old, becomes, new" instead of "old, right arrow, new"
 *     — the visible glyph is decoration; <del>/<ins> carry the semantics.
 *   - New value: <ins> with `bg-success/10` (semantic green tint) and
 *     a slightly heavier weight so the eye lands on the destination value.
 *   - "Empty" old value: when the field had no prior value, we render
 *     a literal "(empty)" placeholder rather than collapsing the column,
 *     so the diff layout stays stable. The label is also wrapped in
 *     <del> so SR semantics still announce a deletion.
 *
 * NO TAILWIND CONFIG TOKENS:
 *   We deliberately use the existing Tailwind `success` semantic token
 *   (defined in `src/tailwind.config.js` → `--success`). No new chat-
 *   namespace token is added — Stream 0 already exposes `chat-*` for
 *   citations and refusals; reusing `success` for "value just got
 *   committed" matches how confidence dots already use it (cited =
 *   `--success`).
 */
import React from "react";

import { cn } from "@/lib/utils";

/**
 * One field change being confirmed. The card may render N of these,
 * stacked. Field is the human-readable label (e.g. "Boiler replaced
 * after 2020"), not the wire-level snake_case key — formatting the
 * label is the caller's job (Phase 2 wiring will compose this from
 * the building schema's `verbose_name`).
 */
export interface FieldChange {
  /** Human-readable field label, already localised. */
  field: string;
  /** Prior value as a string. `null`/`undefined`/`""` render as "(empty)". */
  oldValue: string | null | undefined;
  /** New value as a string. Must be non-empty (caller's contract). */
  newValue: string;
}

export interface ConfirmationCardDiffProps {
  /** One or more field changes to render, top-to-bottom. */
  changes: ReadonlyArray<FieldChange>;
  /** Optional className for layout overrides; merged via `cn`. */
  className?: string;
}

/**
 * Format an old value for display. Empty / null / undefined → "(empty)"
 * placeholder so the layout stays balanced. We do NOT trim the string
 * here — whitespace-only old values are still meaningful (e.g., a field
 * that previously held "  " might be rendered intentionally to show the
 * delete fixes a whitespace-only entry).
 */
const formatOldValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === "") {
    return "(empty)";
  }
  return value;
};

const ConfirmationCardDiff: React.FC<ConfirmationCardDiffProps> = ({
  changes,
  className,
}) => {
  if (changes.length === 0) return null;
  return (
    <ul
      data-testid="confirmation-card-diff"
      className={cn("flex flex-col gap-1.5", className)}
    >
      {changes.map((change, idx) => {
        const oldDisplay = formatOldValue(change.oldValue);
        const isEmptyOld =
          change.oldValue === null ||
          change.oldValue === undefined ||
          change.oldValue === "";
        return (
          <li
            key={`${change.field}-${idx}`}
            data-testid="confirmation-card-diff-row"
            data-field={change.field}
            className="flex flex-wrap items-baseline gap-1.5 text-[14px] leading-[21px]"
          >
            <span className="font-medium text-foreground/80">
              {change.field}:
            </span>
            <del
              data-testid="confirmation-card-diff-old"
              className={cn(
                "line-through decoration-muted-foreground/60",
                "text-muted-foreground",
                // Empty placeholder gets italic styling so it reads as
                // metadata, not as a literal previous value of the
                // string "(empty)".
                isEmptyOld && "italic",
              )}
            >
              {oldDisplay}
            </del>
            <span aria-hidden="true" className="text-muted-foreground">
              →
            </span>
            <ins
              data-testid="confirmation-card-diff-new"
              className={cn(
                "rounded px-1 no-underline",
                // Semantic success tint at 10% — matches the "value just
                // got committed" affordance used by the confidence dot
                // for cited content.
                "bg-success/10 text-foreground",
                "font-medium",
              )}
            >
              {change.newValue}
            </ins>
          </li>
        );
      })}
    </ul>
  );
};

export default ConfirmationCardDiff;
